var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/translation-entry.ts
import { Change } from "@adornis/base/change.js";
import { A } from "@adornis/base/env-info.js";
import { Arg, Field, Mutation, Subscription } from "@adornis/baseql/decorators.js";
import { Entity } from "@adornis/baseql/decorators/entity.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { registerMutation, registerSubscription } from "@adornis/baseql/metadata/register.js";
import { clientTranslator } from "@adornis/translation-core/client/clientTranslator.js";
import { globalLanguageSubject } from "@adornis/translation-core/client/languageSubject.js";
import { validate } from "@adornis/validation/decorators.js";
import { nonOptional } from "@adornis/validation/functions/nonOptional.js";
import {
  BehaviorSubject,
  Subject,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  switchMap,
  take,
  tap,
  timer
} from "rxjs";
import {
  resetTranslationResolver,
  setTranslationResolver,
  subscribeTranslationResolver,
  subscribeTranslationTableResolver,
  upsertTranslationsResolver
} from "../server/resolver/translationEntry.js";
var LanguageTranslationPair = class extends AdornisEntity {
  static resolveID(entity) {
    return entity._id = A.getGloballyUniqueID();
  }
};
LanguageTranslationPair._class = "LanguageTranslationPair";
__decorateClass([
  Field((type) => String)
], LanguageTranslationPair.prototype, "language", 2);
__decorateClass([
  Field((type) => String)
], LanguageTranslationPair.prototype, "translation", 2);
__decorateClass([
  Field((type) => Boolean)
], LanguageTranslationPair.prototype, "overrideByUser", 2);
LanguageTranslationPair = __decorateClass([
  Entity()
], LanguageTranslationPair);
var TranslationTableRow = class extends AdornisEntity {
  get(language) {
    return this.translations.find((e) => e.language === language);
  }
  get isOverridden() {
    return this.translations.some((t) => t.overrideByUser);
  }
};
TranslationTableRow._class = "TranslationTableRow";
__decorateClass([
  Field((type) => [String])
], TranslationTableRow.prototype, "keyPath", 2);
__decorateClass([
  Field((type) => [LanguageTranslationPair])
], TranslationTableRow.prototype, "translations", 2);
TranslationTableRow = __decorateClass([
  Entity()
], TranslationTableRow);
var TranslationEntry = class extends MongoEntity {
  constructor(doc) {
    super(doc);
  }
  static upsertTranslations(translations) {
    return upsertTranslationsResolver(translations);
  }
  /** @deprecated use standalone function */
  static subscribeTranslationDictionary() {
    return subscribeTranslationDictionary();
  }
  /**
   * subscribes currently selected language and updates client translation dictionary accordingly
   * @returns loading observable `true | false`
   * @deprecated use standalone function
   */
  static subscribeTranslationDictionaryAndUpdateClientTranslator() {
    return subscribeTranslationDictionaryAndUpdateClientTranslator();
  }
  /**
   * switch language with automatic loader handling if necessary
   *
   * @deprecated use standalone function
   */
  static async switchLanguage(language) {
    return switchLanguage(language);
  }
  static subscribeTranslation(language) {
    return subscribeTranslationResolver(language);
  }
  static subscribeTranslationTable(languages) {
    return subscribeTranslationTableResolver(languages);
  }
  static setTranslation(language, keyPath, translation) {
    return setTranslationResolver(language, keyPath, translation);
  }
  static resetTranslation(keyPath) {
    return resetTranslationResolver(keyPath);
  }
};
TranslationEntry._class = "TranslationEntry";
TranslationEntry._collectionName = "translation-entries";
__decorateClass([
  Field((type) => [String]),
  validate(nonOptional())
], TranslationEntry.prototype, "keyPath", 2);
__decorateClass([
  Field((type) => String),
  validate(nonOptional())
], TranslationEntry.prototype, "language", 2);
__decorateClass([
  Field((type) => String)
], TranslationEntry.prototype, "translation", 2);
__decorateClass([
  Field((type) => Boolean)
], TranslationEntry.prototype, "overrideByUser", 2);
__decorateClass([
  Field((type) => String)
], TranslationEntry.prototype, "originalValue", 2);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("translations", (type) => [TranslationEntry]))
], TranslationEntry, "upsertTranslations", 1);
__decorateClass([
  Subscription((type) => [TranslationEntry]),
  __decorateParam(0, Arg("language", (type) => String))
], TranslationEntry, "subscribeTranslation", 1);
__decorateClass([
  Subscription((type) => [TranslationTableRow]),
  __decorateParam(0, Arg("languages", (type) => [String]))
], TranslationEntry, "subscribeTranslationTable", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("language", (type) => String)),
  __decorateParam(1, Arg("keyPath", (type) => [String])),
  __decorateParam(2, Arg("translation", (type) => String))
], TranslationEntry, "setTranslation", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("keyPath", (type) => [String]))
], TranslationEntry, "resetTranslation", 1);
TranslationEntry = __decorateClass([
  Entity()
], TranslationEntry);
var upsertTranslations = registerMutation({
  operationName: "upsertTranslations",
  resolve: upsertTranslationsResolver,
  type: () => Boolean,
  params: [{ name: "translations", type: () => [TranslationEntry] }]
});
var subscribeTranslation = registerSubscription({
  operationName: "subscribeTranslation",
  resolve: subscribeTranslationResolver,
  type: () => [TranslationEntry],
  params: [{ name: "language", type: () => String }]
});
var subscribeTranslationTable = registerSubscription({
  operationName: "subscribeTranslationTable",
  resolve: subscribeTranslationTableResolver,
  type: () => [TranslationTableRow],
  params: [{ name: "languages", type: () => [String] }]
});
var setTranslation = registerMutation({
  operationName: "setTranslation",
  resolve: setTranslationResolver,
  type: () => Boolean,
  params: [
    { name: "language", type: () => String },
    { name: "keyPath", type: () => [String] },
    { name: "translation", type: () => String }
  ]
});
var resetTranslation = registerMutation({
  operationName: "resetTranslation",
  resolve: resetTranslationResolver,
  type: () => Boolean,
  params: [{ name: "keyPath", type: () => [String] }]
});
var subscribeTranslationDictionary = () => {
  return globalLanguageSubject.pipe(
    distinctUntilChanged(),
    switchMap(
      (l) => subscribeTranslation(l)({ language: 1, translation: 1, keyPath: 1 }).pipe(
        map((translations) => [l, translations])
      )
    ),
    map(
      ([language, entries]) => entries.reduce((dict, curr) => {
        new Change([curr.translation], curr.keyPath).applyToContext(dict, true);
        return dict;
      }, {})
    )
  );
};
var fetchedLanguages = /* @__PURE__ */ new Set();
var translationDictionarySubscription;
var subscribeTranslationDictionaryAndUpdateClientTranslator = () => {
  const loading = new BehaviorSubject(false);
  if (translationDictionarySubscription)
    translationDictionarySubscription.unsubscribe();
  translationDictionarySubscription = globalLanguageSubject.pipe(
    distinctUntilChanged(),
    tap((language) => loading.next(false)),
    switchMap(
      (l) => subscribeTranslation(l)({ language: 1, translation: 1, keyPath: 1 }).pipe(
        map((translations) => [l, translations])
      )
    ),
    switchMap(async ([language, entries]) => {
      fetchedLanguages.add(language);
      const { clientTranslator: clientTranslator2 } = await import("@adornis/translation-core/client/clientTranslator.js");
      const newDictionary = entries.reduce((dict, curr) => {
        new Change(curr.translation, [
          ...curr.keyPath,
          // @ts-expect-error internal usage
          clientTranslator2._options.languages.indexOf(language)
        ]).applyToContext(dict, true);
        return dict;
      }, clientTranslator2._options.dictionary ?? {});
      clientTranslator2.updateDictionary(newDictionary);
      loading.next(false);
    })
  ).subscribe({ next: () => {
  }, error: () => loading.next(false) });
  return loading;
};
var switchLanguage = async (language) => {
  const loaderTransitionDuration = 200;
  const stopLoading = new Subject();
  if (!fetchedLanguages.has(language)) {
    const { LoaderController } = await import("@adornis/chemistry/controllers/loader-controller.js");
    void LoaderController.waitFor(firstValueFrom(stopLoading));
    await firstValueFrom(timer(loaderTransitionDuration));
  }
  const translationLoading = subscribeTranslationDictionaryAndUpdateClientTranslator();
  await clientTranslator.selectLanguage(language);
  translationLoading.pipe(
    filter((x) => !x),
    take(1)
  ).subscribe((isLoading) => stopLoading.next());
};
export {
  LanguageTranslationPair,
  TranslationEntry,
  TranslationTableRow,
  resetTranslation,
  setTranslation,
  subscribeTranslation,
  subscribeTranslationDictionary,
  subscribeTranslationDictionaryAndUpdateClientTranslator,
  subscribeTranslationTable,
  switchLanguage,
  upsertTranslations
};
